import type {
  MediaAsset,
  MediaReviewStatus,
  ReviewQueueItem,
  UserApplication,
  VerificationBundle,
  VerificationStatus,
} from "../domain/types";
import { matchStore } from "../matching/matchStore";
import { localMediaDb } from "../media/localMediaDb";
import { resolveStoredPreviewUrl, toPersistedMediaAsset } from "../media/uploadService";

const STORAGE_KEY = "un8.reviewQueue.v1";
const APPS_KEY = "un8.applications.v1";

/** Live blob: objectUrls for the current browser session (localStorage cannot hold blobs). */
const sessionApps = new Map<string, UserApplication>();
/** Extra preview URLs keyed by media asset id (survives serialize strip within session). */
const previewUrls = new Map<string, string>();

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  }
}

export type ReviewFilter = "pending_review" | "approved" | "rejected" | "needs_resubmit" | "all";

export interface ReviewQueueStore {
  submitApplication(app: UserApplication): ReviewQueueItem;
  listPending(): ReviewQueueItem[];
  listAll(): ReviewQueueItem[];
  listByStatus(filter: ReviewFilter): ReviewQueueItem[];
  getApplication(applicationId: string): UserApplication | null;
  /** All applications (for pool sync). */
  listApplications(): UserApplication[];
  updateVerificationStatus(
    applicationId: string,
    status: VerificationStatus,
    note?: string
  ): void;
  resolvePreviewUrl(asset: MediaAsset): string;
  /** Hydrate preview from IndexedDB / CDN when blob URL is gone. */
  ensurePreview(asset: MediaAsset): Promise<string>;
  seedDemoIfEmpty(): number;
  clearAll(): void;
  subscribe(listener: Listener): () => void;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / private mode
  }
}

function rememberPreviews(app: UserApplication) {
  const v = app.verification;
  const remember = (asset: MediaAsset | null | undefined) => {
    if (!asset) return;
    const url = asset.publicUrl || asset.objectUrl;
    if (url) previewUrls.set(asset.id, url);
  };
  remember(v.governmentId);
  remember(v.confirmationVideo);
  for (const p of v.profilePhotos) remember(p);
}

function serializeAsset(asset: MediaAsset): MediaAsset {
  // Keep storageKey + publicUrl; strip ephemeral blob: objectUrls for localStorage.
  return toPersistedMediaAsset({
    ...asset,
    uploadStatus: asset.uploadStatus ?? (asset.storageKey ? "uploaded" : "pending"),
  });
}

function serializeApp(app: UserApplication): UserApplication {
  const v = app.verification;
  const userId = app.userId || app.id;
  return {
    ...app,
    id: userId,
    userId,
    passwordSet: app.passwordSet,
    verification: {
      ...v,
      userId,
      governmentId: v.governmentId ? serializeAsset(v.governmentId) : null,
      confirmationVideo: v.confirmationVideo ? serializeAsset(v.confirmationVideo) : null,
      profilePhotos: v.profilePhotos.map(serializeAsset),
    },
  };
}

function cloneApp(app: UserApplication): UserApplication {
  return structuredClone
    ? structuredClone(app)
    : (JSON.parse(JSON.stringify(app)) as UserApplication);
}

function hydratePreviews(app: UserApplication): UserApplication {
  const v = app.verification;
  const restore = (asset: MediaAsset | null): MediaAsset | null => {
    if (!asset) return null;
    if (asset.objectUrl) return asset;
    const cached = previewUrls.get(asset.id);
    return cached ? { ...asset, objectUrl: cached } : asset;
  };
  return {
    ...app,
    verification: {
      ...v,
      governmentId: restore(v.governmentId),
      confirmationVideo: restore(v.confirmationVideo),
      profilePhotos: v.profilePhotos.map((p) => restore(p)!),
    },
  };
}

function toQueueItem(app: UserApplication): ReviewQueueItem {
  const v = app.verification;
  const mediaIds: string[] = [];
  if (v.governmentId) mediaIds.push(v.governmentId.id);
  if (v.confirmationVideo) mediaIds.push(v.confirmationVideo.id);
  for (const p of v.profilePhotos) mediaIds.push(p.id);

  return {
    applicationId: app.id,
    userId: app.userId || app.id,
    email: app.email,
    firstName: app.firstName,
    submittedAt: v.submittedAt ?? app.updatedAt,
    status: v.status,
    mediaSummary: {
      hasId: Boolean(v.governmentId),
      hasVideo: Boolean(v.confirmationVideo),
      photoCount: v.profilePhotos.length,
    },
    mediaIds,
  };
}

function loadQueue(): ReviewQueueItem[] {
  return readJson<ReviewQueueItem[]>(STORAGE_KEY, []);
}

function loadApps(): Record<string, UserApplication> {
  return readJson<Record<string, UserApplication>>(APPS_KEY, {});
}

function applyMediaStatus(
  asset: MediaAsset | null,
  status: MediaReviewStatus,
  note: string | null
): MediaAsset | null {
  if (!asset) return null;
  return { ...asset, reviewStatus: status, reviewNote: note };
}

function mapAppStatus(
  status: VerificationStatus,
  current: UserApplication["status"]
): UserApplication["status"] {
  if (status === "approved") return "verified";
  if (status === "rejected") return "rejected";
  if (status === "pending_review" || status === "needs_resubmit") return "pending_review";
  return current;
}

function emptyValueAnswers() {
  return { loyalty: null, honesty: null, passion: null, commitment: null };
}

function demoAsset(
  kind: MediaAsset["kind"],
  name: string,
  mime: string
): MediaAsset {
  return {
    id: `${kind}_${Math.random().toString(36).slice(2, 8)}`,
    kind,
    fileName: name,
    mimeType: mime,
    sizeBytes: 120_000,
    objectUrl: "",
    storageKey: null,
    publicUrl: null,
    uploadStatus: "pending",
    uploadSource: null,
    uploadError: null,
    reviewStatus: "pending_review",
    createdAt: new Date().toISOString(),
    reviewNote: null,
  };
}

/**
 * Client-side review queue. Swap for HTTP API later.
 */
export const reviewQueueStore: ReviewQueueStore = {
  submitApplication(app: UserApplication): ReviewQueueItem {
    const now = new Date().toISOString();
    const verification: VerificationBundle = {
      ...app.verification,
      status: "pending_review",
      submittedAt: now,
      governmentId: app.verification.governmentId
        ? { ...app.verification.governmentId, reviewStatus: "pending_review" }
        : null,
      confirmationVideo: app.verification.confirmationVideo
        ? { ...app.verification.confirmationVideo, reviewStatus: "pending_review" }
        : null,
      profilePhotos: app.verification.profilePhotos.map((p) => ({
        ...p,
        reviewStatus: "pending_review" as const,
      })),
    };

    const userId = app.userId || app.id;
    const finalized: UserApplication = {
      ...app,
      id: userId,
      userId,
      status: "pending_review",
      updatedAt: now,
      verification: {
        ...verification,
        userId,
      },
      valueAnswers: app.valueAnswers ?? emptyValueAnswers(),
    };

    rememberPreviews(finalized);
    sessionApps.set(finalized.id, cloneApp(finalized));

    const apps = loadApps();
    apps[finalized.id] = serializeApp(finalized);
    writeJson(APPS_KEY, apps);

    const item = toQueueItem(finalized);
    const queue = loadQueue().filter((q) => q.applicationId !== item.applicationId);
    queue.unshift(item);
    writeJson(STORAGE_KEY, queue);
    notify();
    return item;
  },

  listPending(): ReviewQueueItem[] {
    return loadQueue().filter((q) => q.status === "pending_review");
  },

  listAll(): ReviewQueueItem[] {
    return loadQueue();
  },

  listByStatus(filter: ReviewFilter): ReviewQueueItem[] {
    const all = loadQueue();
    if (filter === "all") return all;
    return all.filter((q) => q.status === filter);
  },

  getApplication(applicationId: string): UserApplication | null {
    const live = sessionApps.get(applicationId);
    if (live) return hydratePreviews(cloneApp(live));
    const stored = loadApps()[applicationId];
    return stored ? hydratePreviews(stored) : null;
  },

  listApplications(): UserApplication[] {
    const apps = loadApps();
    const ids = new Set<string>([...Object.keys(apps), ...sessionApps.keys()]);
    return [...ids]
      .map((id) => this.getApplication(id))
      .filter((a): a is UserApplication => Boolean(a));
  },

  updateVerificationStatus(
    applicationId: string,
    status: VerificationStatus,
    note?: string
  ): void {
    const live = sessionApps.get(applicationId);
    const apps = loadApps();
    const stored = apps[applicationId];
    const app = live ?? stored;
    if (!app) return;

    const now = new Date().toISOString();
    const reviewNote = note?.trim() ? note.trim() : null;
    const mediaStatus: MediaReviewStatus =
      status === "approved"
        ? "approved"
        : status === "rejected"
          ? "rejected"
          : status === "needs_resubmit"
            ? "needs_resubmit"
            : "pending_review";

    const verification: VerificationBundle = {
      ...app.verification,
      status,
      reviewedAt: now,
      reviewNote: reviewNote ?? app.verification.reviewNote,
      reviewerId: "local-reviewer",
      governmentId: applyMediaStatus(app.verification.governmentId, mediaStatus, reviewNote),
      confirmationVideo: applyMediaStatus(
        app.verification.confirmationVideo,
        mediaStatus,
        reviewNote
      ),
      profilePhotos: app.verification.profilePhotos.map(
        (p) => applyMediaStatus(p, mediaStatus, reviewNote)!
      ),
    };

    const updated: UserApplication = {
      ...app,
      status: mapAppStatus(status, app.status),
      updatedAt: now,
      verification,
    };

    rememberPreviews(updated);
    sessionApps.set(applicationId, cloneApp(updated));
    apps[applicationId] = serializeApp(updated);
    writeJson(APPS_KEY, apps);

    const queue = loadQueue().map((q) =>
      q.applicationId === applicationId ? { ...q, status } : q
    );
    writeJson(STORAGE_KEY, queue);

    // Approve → persistent matching pool (isEligibleForMatching); reject/resubmit → remove only this id.
    matchStore.syncFromApplication(updated);

    notify();
  },

  resolvePreviewUrl(asset: MediaAsset): string {
    if (asset.publicUrl) {
      const u = asset.publicUrl.trim();
      // Relative /api/media paths (ticket or cookie) — usable as <img src>.
      if (u.startsWith("/")) return u;
      return u;
    }
    if (asset.objectUrl) return asset.objectUrl;
    if (asset.storageKey) {
      const fromIdb = localMediaDb.peekBlobUrl(asset.storageKey);
      if (fromIdb) return fromIdb;
    }
    const cached = previewUrls.get(asset.id);
    if (cached) return cached;
    // Last resort for durable uploads: same-origin private view by id.
    if (asset.id && (asset.uploadStatus === "uploaded" || asset.storageKey)) {
      return `/api/media/${encodeURIComponent(asset.id)}`;
    }
    return "";
  },

  async ensurePreview(asset: MediaAsset): Promise<string> {
    const cached = this.resolvePreviewUrl(asset);
    if (cached) return cached;
    const url = await resolveStoredPreviewUrl(asset);
    if (url) {
      previewUrls.set(asset.id, url);
      // Also warm IDB peek cache when storageKey present
      if (asset.storageKey && url.startsWith("blob:")) {
        // url already cached inside localMediaDb.getBlobUrl
      }
      return url;
    }
    return "";
  },

  seedDemoIfEmpty(): number {
    if (loadQueue().length > 0) return 0;
    const now = new Date().toISOString();
    const demos: UserApplication[] = [1, 2].map((n) => {
      const id = `user_demo_${n}`;
      const photos = Array.from({ length: 7 }, (_, i) =>
        demoAsset("profile_photo", `demo-photo-${n}-${i + 1}.jpg`, "image/jpeg")
      );
      return {
        id,
        userId: id,
        status: "pending_review",
        createdAt: now,
        updatedAt: now,
        isChristian: true,
        rules: {
          respectful: true,
          noSexualContent: true,
          noDominance: true,
          acceptedAt: now,
        },
        intent: "Marriage-minded — I am seeking a partner for a lasting covenant.",
        email: `demo${n}@un8.local`,
        passwordSet: true,
        verification: {
          id: `ver_demo_${n}`,
          userId: id,
          status: "pending_review",
          governmentId: demoAsset("government_id", `demo-id-${n}.jpg`, "image/jpeg"),
          confirmationVideo: demoAsset(
            "confirmation_video",
            `demo-video-${n}.webm`,
            "video/webm"
          ),
          profilePhotos: photos,
          submittedAt: now,
          reviewedAt: null,
          reviewerId: null,
          reviewNote: null,
        },
        whyChristian: "Faith shapes how I love and commit.",
        scales: { loyalty: 8, honesty: 9, passion: 7, commitment: 9 },
        valueAnswers: {
          loyalty: "Presence when it costs — I stay when leaving would be easier.",
          honesty: "Early, even when awkward — Delay is a form of deceit.",
          passion: "Ordered under purpose — Feeling serves commitment.",
          commitment: "A covenant, not a contract — I seek a bond that outlasts convenience.",
        },
        aboutYou: "Demo applicant for local review console.",
        lookingFor: "Someone faithful and clear.",
        loveReady: true,
        hobbies: "Walking, reading",
        reading: 7,
        profession: "Demo role",
        firstName: n === 1 ? "Jordan" : "Alex",
        age: n === 1 ? "29" : "31",
        city: n === 1 ? "Austin" : "Chicago",
        gender: n === 1 ? "male" : "female",
        seeking: n === 1 ? "female" : "male",
      };
    });

    for (const app of demos) {
      this.submitApplication(app);
    }
    return demos.length;
  },

  clearAll(): void {
    sessionApps.clear();
    previewUrls.clear();
    writeJson(STORAGE_KEY, []);
    writeJson(APPS_KEY, {});
    notify();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
